export const VOICE_SAMPLE_RATE = 48_000;
export const VOICE_MAX_FRAMES = 2_880_000;
export const VOICE_CHUNK_FRAMES = 12_000;
export const VOICE_TTL_MS = 86_400_000;

/** Constructed only from validated durable PCM, never from a selected/imported File. */
export class CapturedRecording {
  private constructor(readonly wav: Blob, readonly frames: number) {}
  static fromPcm(chunks: readonly ArrayBuffer[], frames: number) { return new CapturedRecording(voiceWav(chunks, frames), frames); }
}

/** Only committed, contiguous little-endian mono PCM is assembled for playback/upload. */
export function voiceWav(chunks: readonly ArrayBuffer[], frames: number): Blob {
  if (!Number.isInteger(frames) || frames < 1 || frames > VOICE_MAX_FRAMES
    || chunks.some((chunk) => chunk.byteLength < 2 || chunk.byteLength > VOICE_CHUNK_FRAMES * 2 || chunk.byteLength % 2 !== 0)
    || chunks.reduce((size, chunk) => size + chunk.byteLength, 0) !== frames * 2) throw new Error('Invalid durable voice samples.');
  const header = new ArrayBuffer(44); const view = new DataView(header);
  const ascii = (offset: number, value: string) => { for (let index = 0; index < value.length; index += 1) view.setUint8(offset + index, value.charCodeAt(index)); };
  ascii(0, 'RIFF'); view.setUint32(4, 36 + frames * 2, true); ascii(8, 'WAVE'); ascii(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, VOICE_SAMPLE_RATE, true); view.setUint32(28, VOICE_SAMPLE_RATE * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true); ascii(36, 'data'); view.setUint32(40, frames * 2, true);
  return new Blob([header, ...chunks], { type: 'audio/wav' });
}

/** Server time plus monotonic elapsed time; wall-clock edits never extend a clip's life. */
export class VoiceClock {
  private anchor: number | null = null;
  private started = 0;
  private high = 0;
  constructor(private readonly monotonic: () => number = () => performance.now()) {}
  synchronize(serverNow: string) {
    const time = Date.parse(serverNow); if (!Number.isFinite(time)) throw new Error('Voice server time is unavailable.');
    this.anchor = Math.max(this.high, time); this.high = this.anchor; this.started = this.monotonic();
  }
  now() { if (this.anchor === null) throw new Error('Verify the account and server time before recording.'); this.high = Math.max(this.high, this.anchor + Math.max(0, this.monotonic() - this.started)); return this.high; }
}
